import { useEffect, useMemo, useState } from "react";
import type { StoreApi } from "zustand";
import { revalidateLogic, useForm } from "@tanstack/react-form";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import {
  HttpError,
  retryAfterSeconds,
  useAuthStore,
  type AuthRepository,
  type AuthState,
} from "@nasam/core";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  formatCountdown,
  useCountdown,
} from "@nasam/ui";

import { authRepository as defaultAuthRepository } from "../../auth";
import { AuthCardLayout, RequiredMark } from "./AuthCardLayout";
import { retryAfterSecondsFromMessage } from "./retryAfter";

// The backend's own escalating cooldown starts at 5 minutes once the
// 3-requests/hour allowance is used up -- a sane floor if a 429 ever arrives
// without a parseable Retry-After header.
const DEFAULT_RATE_LIMIT_SECONDS = 5 * 60;

export interface ForgotPasswordScreenProps {
  readonly authRepository?: AuthRepository;
  readonly authStore?: StoreApi<AuthState>;
}

export function ForgotPasswordScreen({
  authRepository = defaultAuthRepository,
  authStore = useAuthStore,
}: ForgotPasswordScreenProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { secondsLeft, isActive: isRateLimited, start } = useCountdown();

  const schema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, t("validation:emailRequired"))
          .email(t("validation:emailInvalid")),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const onSubmit = async (values: FormValues) => {
    setErrorMessage(null);
    const email = values.email.trim();
    try {
      await authRepository.forgotPassword({ email });
      authStore.getState().beginPasswordReset(email);
      void navigate({ to: "/auth/verify-reset-otp" });
    } catch (err) {
      if (err instanceof HttpError && err.status === 429) {
        // The button's own countdown already says "try again in N" -- no
        // need for a second, redundant banner repeating it.
        start(
          retryAfterSeconds(err) ??
            retryAfterSecondsFromMessage(err.message) ??
            DEFAULT_RATE_LIMIT_SECONDS,
        );
        return;
      }
      setErrorMessage(
        err instanceof Error ? err.message : t("common:somethingWentWrong"),
      );
    }
  };

  const form = useForm({
    defaultValues: { email: "" } satisfies FormValues,
    validationLogic: revalidateLogic({ mode: "change" }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  useEffect(() => {
    if (!form.state.isValid) void form.validate("change");
  }, [i18n.language, form]);

  const backToLoginLink = (
    <button
      type="button"
      onClick={() => {
        void navigate({ to: "/auth/login" });
      }}
      className="flex h-13 w-full items-center justify-center gap-1 text-body-m-b text-primary hover:underline"
    >
      {t("authentication:backToLogin")}
      <ArrowLeft className="size-6 rtl:rotate-180" aria-hidden="true" />
    </button>
  );

  return (
    <AuthCardLayout
      title={t("authentication:forgotPasswordTitle")}
      subtitle={t("authentication:forgotPasswordSubtitle")}
      illustrationSrc="/brand/password-illustration.png"
      footer={
        <div className="flex flex-col gap-2">
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Button
                type="submit"
                form="forgot-password-form"
                variant="primary"
                className="h-13 w-full"
                disabled={isSubmitting || isRateLimited}
              >
                <span
                  key={isSubmitting ? "busy" : isRateLimited ? "limited" : "idle"}
                  className="animate-in fade-in-0 duration-150"
                >
                  {isSubmitting
                    ? t("authentication:sendingResetCodeButton")
                    : isRateLimited
                      ? t("authentication:sendResetCodeCountdown", {
                          time: formatCountdown(secondsLeft),
                        })
                      : t("authentication:sendResetCodeButton")}
                </span>
              </Button>
            )}
          </form.Subscribe>
          {backToLoginLink}
        </div>
      }
    >
      <Form
        form={form}
        id="forgot-password-form"
        className="flex w-full flex-col gap-8 px-4 md:px-13 py-4"
        noValidate
      >
        {errorMessage !== null && (
          <div
            role="alert"
            className="animate-in fade-in-0 slide-in-from-top-1 rounded-md border border-fail-text bg-container-default-background p-3 text-body-s-r text-fail-text"
          >
            {errorMessage}
          </div>
        )}

        <form.Field name="email">
          {(field) => (
            <FormField field={field}>
              <FormItem className="flex flex-col gap-2 space-y-0">
                <FormLabel
                  htmlFor="forgot-email"
                  className="flex items-center gap-1 text-body-m-r"
                >
                  {t("authentication:emailLabel")}
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <Input
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    placeholder={t("authentication:emailPlaceholder")}
                    className="h-11 bg-container-hover px-4 py-2 text-start text-body-s-r placeholder:text-body-s-r"
                  />
                </FormControl>
                <FormMessage className="text-start" />
              </FormItem>
            </FormField>
          )}
        </form.Field>
      </Form>
    </AuthCardLayout>
  );
}
